import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular/lazy';
import { NotificationPopupComponent } from '@app/shared/components/notification-popup/notification-popup.component';
import { NotificationModel } from '@app/shared/models/notification.model';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';

@Injectable({ providedIn: 'root' })
export class NotificationPopupService {
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalController = inject(ModalController);
  private readonly router = inject(Router);

  private readonly displayed = new Set<string>();
  private processing = false;

  async checkForHome(url: string): Promise<void> {
    if (this.processing || !url.startsWith('/tabs/home')) return;

    const user = this.authService.getStoredUser();
    if (!user?.id) return;

    this.processing = true;
    try {
      const notifications = await this.notificationService.getInfoList();
      const queue = notifications.filter((item) => this.isEligible(item, String(user.id)));

      for (const item of queue) {
        if (!this.router.url.startsWith('/tabs/home')) break;

        const key = this.displayKey(String(user.id), item.id);
        try {
          await this.notificationService.readInfo(item.id);
        } catch {
          continue;
        }

        // 既読化に成功した時点で記録し、モーダル生成失敗時にも再表示しない。
        this.displayed.add(key);

        try {
          const modal = await this.modalController.create({
            component: NotificationPopupComponent,
            componentProps: {
              title: item.title,
              body: item.popupBody,
              categoryLabel: item.categoryLabel,
            },
            cssClass: 'pb-notification-popup-modal',
            backdropDismiss: true,
            htmlAttributes: {
              'aria-labelledby': 'notification-popup-title',
              'aria-describedby': 'notification-popup-message',
            },
          });
          await modal.present();
          const result = await modal.onDidDismiss();

          if (result.role === 'details') {
            await this.router.navigate(['/tabs/notification', item.id], {
              state: { item: { ...item, isRead: true } },
            });
            break;
          }
        } catch (error) {
          console.error('Notification popup failed:', error);
        }
      }
    } catch (error) {
      console.error('Notification popup check failed:', error);
    } finally {
      this.processing = false;
    }
  }

  private isEligible(item: NotificationModel, userId: string): boolean {
    const body = item.popupBody;
    if (item.isRead || typeof body !== 'string' || body.trim() === '') return false;
    if (this.displayed.has(this.displayKey(userId, item.id))) return false;

    if (item.popupExpiresAt) {
      // 既存フロントエンドと同じく Date に直接渡し、現在時刻以上を期限切れとする。
      const expiresAt = new Date(item.popupExpiresAt);
      if (Number.isNaN(expiresAt.getTime()) || new Date() >= expiresAt) return false;
    }

    return true;
  }

  private displayKey(userId: string, notificationId: string | number): string {
    return `${userId}:${notificationId}`;
  }
}
